import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, XCircle, Lightbulb, TrendingDown,
  ArrowRight, Download, Loader2, Package, RefreshCw, FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const TYPE_CONFIG = {
  critical: { color: "red", bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", icon: XCircle, label: "Critique" },
  warning: { color: "amber", bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", icon: AlertTriangle, label: "Attention" },
  success: { color: "green", bg: "bg-green-500/10", border: "border-green-200", text: "text-green-400", icon: CheckCircle2, label: "Bon" },
};

const CATEGORY_LABELS = {
  commande_inverse: "Commande inverse",
  optimisation_stock: "Optimisation stock",
  peremption: "Péremption",
  performance: "Performance",
};

const RecommendationsPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadRecommendations(); }, []);

  const loadRecommendations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/recommendations`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error("Erreur de chargement des recommandations");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/analytics/report/pdf`, {
        headers, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `rapport_analyse_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Rapport PDF téléchargé");
    } catch (err) {
      toast.error("Erreur de génération du rapport");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </Layout>
    );
  }

  const criticalCount = data?.recommendations.filter(r => r.type === "critical").length || 0;
  const warningCount = data?.recommendations.filter(r => r.type === "warning").length || 0;

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="recommendations-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-white flex items-center gap-2">
              <Lightbulb className="w-7 h-7 text-amber-500" />
              Recommandations Intelligentes
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Analyse automatique de vos données pour optimiser votre gestion
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={downloadReport} disabled={downloading} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="download-report-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Rapport PDF
            </Button>
            <Button onClick={loadRecommendations} variant="outline" className="border-cyan-500/20 text-cyan-400">
              <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="medical-card">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-400">{criticalCount}</p>
              <p className="text-xs text-slate-500">Actions critiques</p>
            </CardContent>
          </Card>
          <Card className="medical-card">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{warningCount}</p>
              <p className="text-xs text-slate-500">Points d'attention</p>
            </CardContent>
          </Card>
          <Card className="medical-card">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-cyan-400">{data?.data_years || 0}</p>
              <p className="text-xs text-slate-500">Années analysées</p>
            </CardContent>
          </Card>
          <Card className="medical-card">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-200">{data?.total_products || 0}</p>
              <p className="text-xs text-slate-500">Produits en stock</p>
            </CardContent>
          </Card>
        </div>

        {/* Recommendations */}
        <div className="space-y-4">
          {data?.recommendations.map((reco) => {
            const config = TYPE_CONFIG[reco.type] || TYPE_CONFIG.warning;
            const Icon = config.icon;
            return (
              <Card key={reco.id} className={`${config.bg} ${config.border} border`} data-testid={`reco-${reco.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className={`w-5 h-5 ${config.text}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className={`font-semibold ${config.text}`}>{reco.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${config.bg} ${config.text} font-medium border ${config.border}`}>
                          {config.label}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          {CATEGORY_LABELS[reco.category] || reco.category}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{reco.description}</p>

                      {/* Metrics */}
                      {reco.metrics && Object.keys(reco.metrics).length > 0 && (
                        <div className="flex flex-wrap gap-3 mb-3">
                          {reco.metrics.current_ratio !== undefined && (
                            <div className="bg-white rounded-lg px-3 py-1.5 border border-white/10">
                              <p className="text-[10px] text-slate-400">Actuel</p>
                              <p className={`text-sm font-bold ${config.text}`}>{reco.metrics.current_ratio}%</p>
                            </div>
                          )}
                          {reco.metrics.target_ratio !== undefined && (
                            <div className="bg-white rounded-lg px-3 py-1.5 border border-white/10">
                              <p className="text-[10px] text-slate-400">Objectif</p>
                              <p className="text-sm font-bold text-green-400">{reco.metrics.target_ratio}%</p>
                            </div>
                          )}
                          {reco.metrics.peer_avg !== undefined && (
                            <div className="bg-white rounded-lg px-3 py-1.5 border border-white/10">
                              <p className="text-[10px] text-slate-400">Moy. commune</p>
                              <p className="text-sm font-bold text-blue-400">{reco.metrics.peer_avg}%</p>
                            </div>
                          )}
                          {reco.metrics.potential_savings !== undefined && (
                            <div className="bg-white rounded-lg px-3 py-1.5 border border-green-200">
                              <p className="text-[10px] text-green-500">Économie potentielle</p>
                              <p className="text-sm font-bold text-green-400">{reco.metrics.potential_savings.toLocaleString()} F</p>
                            </div>
                          )}
                          {reco.metrics.value !== undefined && (
                            <div className="bg-white rounded-lg px-3 py-1.5 border border-red-500/20">
                              <p className="text-[10px] text-red-400">Valeur menacée</p>
                              <p className="text-sm font-bold text-red-400">{reco.metrics.value.toLocaleString()} F</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="space-y-1.5">
                        {reco.actions.map((action, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <ArrowRight className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${config.text}`} />
                            <span className="text-sm text-slate-600">{action}</span>
                          </div>
                        ))}
                      </div>

                      {/* Expiring products detail */}
                      {reco.products && reco.products.length > 0 && (
                        <div className="mt-3 bg-white rounded-lg border border-white/10 p-3">
                          <p className="text-xs font-semibold text-slate-500 mb-2">Produits concernés :</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {reco.products.map((p, i) => (
                              <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                                <div className="flex items-center gap-2">
                                  <Package className="w-3 h-3 text-slate-400" />
                                  <span className="text-slate-200">{p.name}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-red-500">Exp: {p.expiry}</span>
                                  <span className="text-slate-500">{p.value.toLocaleString()} F</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Download prompt */}
        <Card className="medical-card bg-gradient-to-r from-cyan-50 to-blue-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <FileText className="w-10 h-10 text-cyan-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-white">Téléchargez votre rapport complet</h3>
                <p className="text-sm text-slate-500">Rapport PDF avec logo CMES, vos données historiques, ratios de performance et recommandations pour une analyse approfondie.</p>
              </div>
              <Button onClick={downloadReport} disabled={downloading} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="download-report-bottom-btn">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RecommendationsPage;
